import { ItauGatewayConfig, onlyDigits } from '../../../models/itau-gateway-config';

export type CertificateState = 'none' | 'valid' | 'expiring' | 'expired';

export interface CertificateStatus {
  state: CertificateState;
  validUntil: Date | null;
  daysLeft: number | null;
}

/** A partir de quantos dias para vencer o certificado aparece como "vence em breve". */
export const CERTIFICATE_EXPIRING_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Converte o que o backend mandar para data: 'AAAA-MM-DD' (dia local, sem fuso),
 * ISO com hora, epoch em ms ou o array do Jackson [a, m, d, h, min, s].
 */
export function parseBackendDate(value: any): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (Array.isArray(value)) {
    if (value.length < 3) {
      return null;
    }
    const [y, m, d, h = 0, min = 0, s = 0] = value.map((v) => Number(v));
    const fromArray = new Date(y, m - 1, d, h, min, s);
    return isNaN(fromArray.getTime()) ? null : fromArray;
  }
  if (typeof value === 'number') {
    const fromEpoch = new Date(value);
    return isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }
  if (typeof value === 'string') {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (dateOnly) {
      return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Dias de calendário de `now` até `date` (negativo se já passou). */
export function daysUntil(date: Date, now: Date = new Date()): number {
  return Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);
}

export function hasCertificate(config: ItauGatewayConfig): boolean {
  return !!config && (!!config.certValidUntil || !!config.certHolder);
}

export function certificateStatus(config: ItauGatewayConfig, now: Date = new Date()): CertificateStatus {
  const validUntil = config ? parseBackendDate(config.certValidUntil) : null;
  if (!validUntil) {
    return { state: 'none', validUntil: null, daysLeft: null };
  }
  const daysLeft = daysUntil(validUntil, now);
  let state: CertificateState = 'valid';
  if (daysLeft < 0) {
    state = 'expired';
  } else if (daysLeft <= CERTIFICATE_EXPIRING_DAYS) {
    state = 'expiring';
  }
  return { state, validUntil, daysLeft };
}

function isBlank(value: any): boolean {
  return value === null || value === undefined || String(value).trim().length === 0;
}

/** Campos obrigatórios para salvar; devolve os rótulos que faltam. */
export function missingRequiredFields(config: ItauGatewayConfig): string[] {
  const c = config || {};
  const missing: string[] = [];
  if (c.billingPortfolioId == null) { missing.push('Carteira de cobrança'); }
  if (c.paymentMethod == null) { missing.push('Forma de pagamento'); }
  if (c.account == null) { missing.push('Conta para crédito'); }
  if (isBlank(c.branchNumber)) { missing.push('Agência'); }
  if (isBlank(c.accountNumber)) { missing.push('Conta'); }
  if (isBlank(c.accountNumberVd)) { missing.push('DV'); }
  if (isBlank(c.wallet)) { missing.push('Carteira'); }
  if (isBlank(c.beneficiaryDocument)) { missing.push('CNPJ do cedente'); }
  if (isBlank(c.beneficiaryName)) { missing.push('Nome do cedente'); }
  return missing;
}

/** CNPJ com 14 dígitos e dígitos verificadores corretos. */
export function isValidCnpj(value: any): boolean {
  const digits = onlyDigits(value);
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) {
    return false;
  }
  const calc = (length: number): number => {
    const weights = length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < length; i++) {
      sum += Number(digits.charAt(i)) * weights[i];
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return calc(12) === Number(digits.charAt(12)) && calc(13) === Number(digits.charAt(13));
}

/**
 * Por que o botão Ativar está bloqueado (lista vazia = pode ativar).
 * O backend confere de novo; aqui é só para orientar quem usa a tela.
 */
export function activationBlockers(config: ItauGatewayConfig, dirty: boolean, now: Date = new Date()): string[] {
  const blockers: string[] = [];
  if (!config || config.id == null) {
    blockers.push('Salve a configuração primeiro.');
    return blockers;
  }
  if (config.active) {
    blockers.push('A configuração já está ativa.');
    return blockers;
  }
  if (dirty) {
    blockers.push('Salve as alterações antes de ativar.');
  }
  const cert = certificateStatus(config, now);
  if (cert.state === 'none') {
    blockers.push('Gere o certificado no gateway.');
  } else if (cert.state === 'expired') {
    blockers.push('O certificado está vencido. Gere um novo.');
  }
  if (config.lastTestOk !== true) {
    blockers.push('Teste a conexão com sucesso antes de ativar.');
  }
  return blockers;
}
